import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DocPurpose as DocPurposeInterface } from '../../../interfaces/invoice.interface';
import { PAYMENT_METHOD_AADE_CODE } from '../../../interfaces/paymentTypes.interface';
import { DOC_PURPOSE_AADE_CODE } from '../../../services/utils/docPurposeUtils';
import { isDocTypeInvertedDeliveryNote } from '../../../services/utils/myDataUtils';
import { CorrelatedInvoicesSection } from './CorrelatedInvoicesSection';
import { DeliveryNoteToggle } from './DeliveryNoteToggle';
import { DocDate } from './DocDate';
import { DocPaymentType } from './DocPaymentType';
import { DocPos } from './DocPos';
import { DocPurpose } from './DocPurpose';
import { DocQuantityReceipt } from './DocQuantityReceipt';
import { DocSeries } from './DocSeries';
import { DocType } from './DocType';
import { TransitToggles } from './TransitToggles';

interface Props {
  type: any;
  purposeField: any;
  date: any;
  paymentField: any;
  seriesName: any;
  serialNumber: any;
  posField: any;
  purposeField2: any;
  receivingNotePurposeField: any;
  otherReceivingNotePurposeTitleField: any;
  isDeliveryNoteField: any;
  resetSerialNumberField: any;
  nonObligatedRecipientField: any;
  withoutDigitalTransportTrackingField: any;
  formik: any;
  isClone?: boolean;
  isCorrelate?: boolean;
}

export const DocMainInfoSection = ({
  type,
  purposeField,
  date,
  paymentField,
  seriesName,
  serialNumber,
  posField,
  purposeField2,
  receivingNotePurposeField,
  otherReceivingNotePurposeTitleField,
  isDeliveryNoteField,
  resetSerialNumberField,
  nonObligatedRecipientField,
  withoutDigitalTransportTrackingField,
  formik,
  isClone,
  isCorrelate,
}: Props) => {
  const { t } = useTranslation();
  const { values } = formik;

  const renderPos = useMemo(() => {
    if (
      formik?.values?.post_with_provider &&
      (formik?.values?.paymentTypeId?.object?.aadeCode === PAYMENT_METHOD_AADE_CODE.POS ||
        formik?.values?.paymentTypeId?.aadeCode === PAYMENT_METHOD_AADE_CODE.POS)
    ) {
      return true;
    }

    return false;
  }, [formik?.values?.post_with_provider, formik?.values?.paymentTypeId]);
  const documentTypeAadeCode = useMemo(
    () => values?.documentTypeId?.object?.aadeCode,
    [values?.documentTypeId?.object?.aadeCode],
  );

  useEffect(() => {
    if (!isDocTypeInvertedDeliveryNote(documentTypeAadeCode)) {
      const current = formik?.values?.[purposeField2.name];
      if (current !== null && current !== undefined) {
        formik?.setFieldValue(purposeField2.name, null);
      }
      return;
    }

    if (isDocTypeInvertedDeliveryNote(documentTypeAadeCode) && purposeField2?.data?.length && !purposeField2?.value) {
      const defaultPurpose2 = {
        label: `(${purposeField2?.data[0]?.aadeCode}) ` + t(`DOC_PURPOSES.${purposeField2?.data[0]?.name}`),
        value: purposeField2?.data[0]?.id,
        object: purposeField2?.data[0],
      };

      formik?.setFieldValue(purposeField2.name, defaultPurpose2);
    }
  }, [documentTypeAadeCode, purposeField2?.data, purposeField2.name, purposeField2?.value, t]);

  // `(9.3) Δελτίο Αποστολής (Αντίστροφο)` (inverted delivery note) means the counterpart is
  // acquiring goods, so its move purpose must be `(9) ΑΓΟΡΑ`. Reverting to `(1) ΠΩΛΗΣΗ` only
  // undoes a value *this effect itself* forced — a regular (non-inverted) delivery note's own
  // movement reason (e.g. DESTRUCTION/DONATION/RETURN) is a completely separate, legitimate use of
  // this same field and must never be clobbered just because the doc type changed to/from it.
  const appliedBuyPurposeRef = useRef(false);

  useEffect(() => {
    const setPurposeTo = (aadeCode: string) => {
      const target = purposeField?.data?.find((entity: DocPurposeInterface) => entity.aadeCode === aadeCode);
      if (!target) return;

      formik?.setFieldValue(purposeField.name, {
        label: `(${target.aadeCode}) ` + t(`DOC_PURPOSES.${target.name}`),
        value: target.id,
        object: target,
      });
    };

    if (isDocTypeInvertedDeliveryNote(documentTypeAadeCode)) {
      appliedBuyPurposeRef.current = true;
      if (formik?.values?.[purposeField.name]?.object?.aadeCode !== DOC_PURPOSE_AADE_CODE.BUY) {
        setPurposeTo(DOC_PURPOSE_AADE_CODE.BUY);
      }
      return;
    }

    if (appliedBuyPurposeRef.current) {
      appliedBuyPurposeRef.current = false;
      setPurposeTo(DOC_PURPOSE_AADE_CODE.SALE);
    }
  }, [documentTypeAadeCode, purposeField?.data, purposeField.name, t]);

  /**
   * Select only when no pre-select is available due to clone
   */
  const preSelectFirstPos = useCallback(() => {
    if (posField?.data?.length > 0 && !posField?.value) {
      const defaultPos = {
        label: posField?.data[0]?.name,
        value: posField?.data[0]?.tid,
        object: posField?.data[0],
      };
      formik?.setFieldValue(posField.name, defaultPos);
    }
  }, [posField?.data, posField.name]);

  useEffect(() => {
    if (renderPos) {
      preSelectFirstPos();
    } else {
      formik?.setFieldValue(posField.name, null);
    }
  }, [posField?.data, posField.name, renderPos, preSelectFirstPos]);

  const getPos = useCallback(() => {
    if (renderPos) {
      return <DocPos field={posField} formik={formik} />;
    }
  }, [formik, posField, renderPos]);

  const getDocPurpose2 = useCallback(() => {
    if (isDocTypeInvertedDeliveryNote(documentTypeAadeCode)) {
      return <DocPurpose field={purposeField2} formik={formik} />;
    }
  }, [documentTypeAadeCode, formik, purposeField2]);

  return (
    <div className="docForm">
      <div className="padding-t-24 padding-b-24 padding-l-8 padding-r-8 flex-start-center-row-wrap gap-30-16">
        <DocType field={type} formik={formik} />
        <DeliveryNoteToggle field={isDeliveryNoteField} formik={formik} />
        <TransitToggles
          nonObligatedRecipientField={nonObligatedRecipientField}
          withoutDigitalTransportTrackingField={withoutDigitalTransportTrackingField}
          formik={formik}
        />
        <DocSeries
          seriesNameField={seriesName}
          serialNumberField={serialNumber}
          resetSerialNumberField={resetSerialNumberField}
          formik={formik}
          isClone={isClone}
          isCorrelate={isCorrelate}
        />
        <DocPurpose field={purposeField} formik={formik} />
        {getDocPurpose2()}
        <DocQuantityReceipt
          receivingNotePurposeField={receivingNotePurposeField}
          otherReceivingNotePurposeTitleField={otherReceivingNotePurposeTitleField}
          formik={formik}
        />
        <DocPaymentType field={paymentField} formik={formik} />
        {getPos()}
        <DocDate field={date} formik={formik} title={`${t('SALE.DOC_DATE')} *`} minDate={dayjs()} />
        <CorrelatedInvoicesSection
          formik={formik}
          customerId={formik?.values?.customerId?.value || formik?.values?.customerId?.id || formik?.values?.customerId}
          initialUiMarks={isCorrelate ? (formik?.values?.correlatedInvoices || []).map((c: any) => c.mark) : undefined}
        />
      </div>

      {/*<div className="margin-t-10">*/}
      {/*TODO: Map of doc types to `Σκοπός έκδοσης` TODO: add missing doc types*/}
      {/*aadeId: 1.1, 1.2, 1.3, 1.12 -> 01 */}
      {/*aadeId: 5.1, 5.2 -> 02/05 */}
      {/*aadeId: 9.3 -> all */}
      {/*aadeId: 1.12 -> 01 */}
      {/*credit invoice return (5.1) Πιστωτικό Τιμολόγιο Επιστροφής Συσχετιζόμενο -> 02/05*/}
      {/*credit invoice return (5.2) Πιστωτικό Τιμολόγιο Επιστροφής Μη Συσχετιζόμενο-> 02/05*/}
      {/*credit invoice sale (5.2) Πιστωτικό Τιμολόγιο Έκπτωσης -> 11*/}
      {/*</div>*/}
    </div>
  );
};
